/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
// import { useMediaListStore } from "@/stores/mediaListStore";
import { useReSearchStore } from "@/stores/reSearchStore";
import { useSearchConditionStore } from "@/stores/searchConditionStore";
import { useApiIsLoaded, useMap } from "@vis.gl/react-google-maps";
import { usePathname } from "next/navigation";
import { useEffect, useReducer, useRef, useState } from "react";
import { useGetAddress } from "@/hooks/map-hooks/useGetAddress";
import { useTypedTranslations } from "@/i18n/typed";
import { useTranslations } from "next-intl";
import { Typography } from "@/components/custom/typography";

/* ---------------------------------------------
 * Types
 * --------------------------------------------- */
type Location = {
  name: string;
  code: string;
};

type State = {
  country: string;
  province: string;
  district: string;
  countryName: string;
  provinceName: string;
  districtName: string;
  searchValue: string;
};

type Action =
  | { type: "SET_COUNTRY"; payload: { code: string; name: string } }
  | { type: "SET_PROVINCE"; payload: { code: string; name: string } }
  | { type: "SET_DISTRICT"; payload: { code: string; name: string } }
  | { type: "SET_SEARCH"; payload: string }
  | { type: "RESET" };

/* ---------------------------------------------
 * Reducer
 * --------------------------------------------- */
function reducer(state: State, action: Action): State {
  switch (action.type) {
    case "SET_COUNTRY":
      return {
        country: action.payload.code,
        province: "",
        district: "",
        countryName: action.payload.name,
        provinceName: "",
        districtName: "",
        searchValue: action.payload.name || "",
      };

    case "SET_PROVINCE":
      return {
        ...state,
        province: action.payload.code,
        district: "",
        provinceName: action.payload.name,
        districtName: "",
        searchValue: action.payload.name
          ? `${action.payload.name}, ${state.countryName}`
          : state.countryName,
      };

    case "SET_DISTRICT":
      return {
        ...state,
        district: action.payload.code,
        districtName: action.payload.name,
        searchValue: action.payload.name
          ? `${action.payload.name}, ${state.provinceName}, ${state.countryName}`
          : state.provinceName || state.countryName,
      };

    case "SET_SEARCH":
      return { ...state, searchValue: action.payload };

    case "RESET":
      return {
        country: "",
        province: "",
        district: "",
        countryName: "",
        provinceName: "",
        districtName: "",
        searchValue: ""
      };

    default:
      return state;
  }
}

/* ---------------------------------------------
 * Component
 * --------------------------------------------- */
export default function LocationSelector() {
  const t = useTranslations("address");
  const tDropDown = useTypedTranslations("dropDown");

  const pathName = usePathname();
  const map = useMap();
  const isLoaded = useApiIsLoaded();

  // const { setBounds: setBoundMediaList } = useMediaListStore();
  const { resetCount, locationResetToken } = useReSearchStore();
  // const { setBounds, resetAll } = useMapStore();
  const { userCountry, setUserCountry } = useSearchConditionStore();

  const [defaultView, setDefaultView] = useState<{
    center: google.maps.LatLngLiteral;
    zoom: number;
  } | null>(null);

  const [state, dispatch] = useReducer(reducer, {
    country: "",
    province: "",
    district: "",
    countryName: "",
    provinceName: "",
    districtName: "",
    searchValue: "",
  });

  const { country, province, district, searchValue } = state;
  const previousUserCountryRef = useRef<string | null>(userCountry);
  const previousResetTokenRef = useRef<number>(locationResetToken);

  /* ---------------------------------------------
   * Load address data
   * --------------------------------------------- */
  const { data: countryDataRaw } = useGetAddress();
  const { data: provinceDataRaw } = useGetAddress(
    country ? { country_code: country } : undefined
  );
  const { data: districtDataRaw } = useGetAddress(
    country && province ? { country_code: country, city_code: province } : undefined
  );
  const countryData = Array.isArray(countryDataRaw?.data)
    ? countryDataRaw.data
    : [];

  const provinceData = Array.isArray(provinceDataRaw?.data)
    ? provinceDataRaw.data
    : [];

  const districtData = Array.isArray(districtDataRaw?.data)
    ? districtDataRaw.data
    : [];

  // Countries
  const countries: Location[] = (countryData || [])
    .filter((c: any) => c?.code)
    .map((c: any) => {
      const key = `country.${c.code}`;
      const hasTranslation = t.has(key);
      return {
        name: hasTranslation ? t(key) : c.name_country,
        code: c.code,
      };
    });

  // Provinces / Cities
  const provinces: Location[] = (provinceData || [])
    .filter((p: any) => p?.code)
    .map((p: any) => {
      const key = `city.${p.code}`;
      const hasTranslation = t.has(key);
      return {
        name: hasTranslation ? t(key) : p.name_city,
        code: p.code,
      };
    });

  // Districts
  const districts: Location[] = (districtData || [])
    .filter((d: any) => d?.code)
    .map((d: any) => {
      const key = `district.${d.code}`;
      const hasTranslation = t.has(key);
      return {
        name: hasTranslation ? t(key) : d.name_district,
        code: d.code,
      };
    });

  // Handle Geocoding (safe)
  const handleGeocode = (address: string) => {
    if (!address || !isLoaded || !map) return;

    try {
      const geocoder = new google.maps.Geocoder();

      geocoder.geocode({ address }, (results, status) => {
        if (status === "OK" && results?.[0]) {
          const { bounds, viewport, location } = results[0].geometry;

          const fit = bounds || viewport;
          if (fit) {
            map.fitBounds(fit);
            // setBounds(fit);
            // setBoundMediaList(fit);
          } else if (location) {
            map.setCenter(location);
            map.setZoom(11);
          }

          resetCount(0);
        } else {
          console.warn("Geocode failed:", status);
        }
      });
    } catch (err) {
      console.error("Geocode error:", err);
    }
  };

  // Save default map view (only once)
  useEffect(() => {
    try {
      if (map && !defaultView) {
        setDefaultView({
          center: map.getCenter()?.toJSON() || { lat: 0, lng: 0 },
          zoom: map.getZoom() || 2,
        });
      }
    } catch {
      // prevent crash
    }
  }, [map, defaultView]);

  // Update map when searchValue changes
  useEffect(() => {
    if (searchValue) handleGeocode(searchValue);
  }, [searchValue]);

  // Reset when returning to root ("/")
  useEffect(() => {
    if (pathName === "/") {
      if (country || province || district || searchValue) {
        dispatch({ type: "RESET" });
        setUserCountry("");

        if (defaultView && map) {
          map.setCenter(defaultView.center);
          map.setZoom(defaultView.zoom);
        }
      }
    }
  }, [pathName, map, defaultView]);

  // Sync with store userCountry & handle global resets (e.g. logo click)
  useEffect(() => {
    if (userCountry && userCountry !== country) {
      const countryObj = countries.find(c => c.code === userCountry);
      if (countryObj) {
        dispatch({ type: "SET_COUNTRY", payload: { code: countryObj.code, name: countryObj.name } });
      }
    }

    const resetSignal =
      (userCountry === "" || userCountry === null) &&
      previousUserCountryRef.current !== userCountry;

    if (resetSignal) {
      dispatch({ type: "RESET" });

      if (map && defaultView) {
        map.setCenter(defaultView.center);
        map.setZoom(defaultView.zoom);
      }
    }

    previousUserCountryRef.current = userCountry;
  }, [userCountry, country, countries, map, defaultView]);

  // Sync with global reset events (e.g. logo click on home page)
  useEffect(() => {
    if (previousResetTokenRef.current === locationResetToken) return;
    previousResetTokenRef.current = locationResetToken;

    dispatch({ type: "RESET" });
    setUserCountry("");

    if (map && defaultView) {
      map.setCenter(defaultView.center);
      map.setZoom(defaultView.zoom);
    }
  }, [locationResetToken, map, defaultView, setUserCountry]);

  // Render
  return (
    <div className="flex top-[var(--header-height)] items-center justify-end fixed translate-x-[-50%] left-[50%] mt-2.5">
      <div className="bg-white rounded-full shadow-xl/20 p-2.5 flex items-center gap-2.5 border border-primary-400 h-11">
        {/* Country */}
        <Select
          value={country}
          onValueChange={(val) => {
            const countryObj = countries.find(c => c.code === val);
            if (countryObj) {
              dispatch({ type: "SET_COUNTRY", payload: { code: countryObj.code, name: countryObj.name } });
            }
          }}
          disabled={!countries.length}
        >
          <SelectTrigger className="w-[100px] text-xs border-0 p-1 shadow-none !h-auto">
            <SelectValue placeholder={tDropDown("locationSelector.country")} />
          </SelectTrigger>
          <SelectContent>
            {countries.map((c) => (
              <SelectItem key={c.code} value={c.code}>
                {c.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Typography className="text-input">/</Typography>

        {/* Province */}
        <Select
          value={province}
          onValueChange={(val) => {
            const provinceObj = provinces.find(p => p.code === val);
            if (provinceObj) {
              dispatch({ type: "SET_PROVINCE", payload: { code: provinceObj.code, name: provinceObj.name } });
            }
          }}
          disabled={!country || !provinces.length}
        >
          <SelectTrigger className="w-[100px] text-xs border-0 p-1 shadow-none !h-auto">
            <SelectValue placeholder={tDropDown("locationSelector.province")} />
          </SelectTrigger>

          <SelectContent>
            {provinces.map((p) => (
              <SelectItem key={p.code} value={p.code}>
                {p.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <span className="text-input">/</span>

        {/* District */}
        <Select
          value={district}
          onValueChange={(val) => {
            const districtObj = districts.find(d => d.code === val);
            if (districtObj) {
              dispatch({ type: "SET_DISTRICT", payload: { code: districtObj.code, name: districtObj.name } });
            }
          }}
          disabled={!province || !districts.length}
        >
          <SelectTrigger className="w-[100px] text-xs border-0 p-1 shadow-none !h-auto">
            <SelectValue placeholder={tDropDown("locationSelector.district")} />
          </SelectTrigger>

          <SelectContent>
            {districts.map((d) => (
              <SelectItem key={d.code} value={d.code}>
                {d.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
